import {
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik, getIn } from "formik";
import { reverse } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import { ROUTE_PATH } from "@/common/constants/routing";
import { HighlightHelper } from "@/common/helpers/highlight";
import useMounted from "@/common/hooks/mount/useMounted";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useCurrentCurrencyV2 } from "@/common/hooks/useCurrentCurrencyV2";
import { cast } from "@/common/ts/conversions";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  GeneralCurrencyInputDto,
  GeneralDiscountInputDto,
  GeneralTaxInputDto,
  RepairOperationAddVehicleDamageDto,
  RepairOperationAddVehicleDamagesDto,
  RepairOperationDto,
  RepairOperationStage,
} from "@/core/api/generated/v1";

import AuthorizedElement from "../../Auth/AuthorizedElement";
import GeneralValidationError from "../../Error/GeneralValidationError";
import PriceSummaryInput from "../../PriceSummary/PriceSummaryInput";
import VehicleDamageLink from "../VehicleDamage/VehicleDamageLink";
import RepairOperationAutocompleteOrCreate from "./RepairOperationAutocompleteOrCreate";
import { RepairOperationViewPageHighlightProps } from "./View/RepairOperationView";

type OwnProps = {
  vehicleId: string;
  currency?: GeneralCurrencyInputDto | null;
  vehicleDamageIdsCosts?: Nil<
    Array<{
      vehicleDamageId: string;
      cost?: number | null;
      discount?: GeneralDiscountInputDto;
      tax?: GeneralTaxInputDto;
    }>
  >;
  vehicleDamageIds: Nil<string[]>;
  isRedirectToRepairOperation?: boolean;
  onSaved?: (repairOperation: RepairOperationDto) => void;
};

type Props = OwnProps & DialogProps;

export function RepairOperationAddVehicleDamagesModal({
  vehicleId,
  currency,
  vehicleDamageIdsCosts,
  vehicleDamageIds,
  isRedirectToRepairOperation = true,
  onSaved,
  ...dialogProps
}: Props) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();
  const history = useAppHistory();
  const currentCurrency = useCurrentCurrencyV2();

  const [isCreatingRepairOperation, setIsCreatingRepairOperation] = useState(false);
  const [repairOperation, setRepairOperation] = useState<RepairOperationDto | undefined>(undefined);

  const vehicleDamageIdsCostComputed = useMemo(
    () =>
      vehicleDamageIdsCosts ||
      vehicleDamageIds?.map((x) =>
        cast<{
          vehicleDamageId: string;
          cost?: number | null;
          discount?: GeneralDiscountInputDto;
          tax?: GeneralTaxInputDto;
        }>({
          vehicleDamageId: x || "",
          cost: undefined,
          discount: undefined,
          tax: undefined,
        }),
      ) ||
      [],
    [vehicleDamageIdsCosts, vehicleDamageIds],
  );

  // Reset state when modal opens
  useEffect(() => {
    if (dialogProps.open) {
      setIsCreatingRepairOperation(false);
      setRepairOperation(undefined);
    }
  }, [dialogProps.open]);

  return (
    <Dialog fullWidth maxWidth='lg' {...dialogProps}>
      <AppModalTitle
        onClose={() => dialogProps?.onClose && dialogProps?.onClose({}, "escapeKeyDown")}
      >
        Add damages to a repair operation
      </AppModalTitle>
      <DialogContent>
        <AuthorizedElement permissions={[AppPermission.RepairOperationManage]} withAlert>
          <Stack spacing={1}>
            <Formik<
              BaseFormikValues &
                Omit<RepairOperationAddVehicleDamagesDto, "vehicleDamages"> & {
                  vehicleDamages?: Array<
                    RepairOperationAddVehicleDamageDto & {
                      total?: number | undefined;
                    }
                  >;
                  repairOperationId?: string | undefined;
                  currency?: GeneralCurrencyInputDto | null | undefined;
                }
            >
              enableReinitialize
              initialValues={{
                repairOperationId: undefined,
                currency: currency || currentCurrency.currency || undefined,
                vehicleDamages:
                  vehicleDamageIdsCosts?.map((x) =>
                    cast<RepairOperationAddVehicleDamageDto>({
                      vehicleDamageId: x.vehicleDamageId,
                      cost: x.cost,
                      discount: x.discount,
                      tax: x.tax,
                    }),
                  ) ||
                  vehicleDamageIds?.map((x) =>
                    cast<RepairOperationAddVehicleDamageDto>({
                      vehicleDamageId: x || "",
                      cost: undefined,
                      discount: undefined,
                      tax: undefined,
                    }),
                  ) ||
                  [],
                submit: "",
              }}
              validationSchema={Yup.object().shape({
                // repairOperationId: Yup.string().required("Required"),
              })}
              onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
                try {
                  const response =
                    await apiClient.repairOperationsApi.apiV1RepairOperationsRepairOperationIdDamagesAddPost(
                      {
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        repairOperationId: values.repairOperationId || "",
                        repairOperationAddVehicleDamagesDto: {
                          vehicleDamages: values.vehicleDamages,
                        },
                      },
                    );
                  const responseRepairOperation = response.data;
                  const addedItem = reverse(responseRepairOperation.items || []).find((x) =>
                    vehicleDamageIdsCostComputed.some(
                      (damage) => damage.vehicleDamageId === x.damage?.id,
                    ),
                  );

                  enqueueSnackbar("Successfully added.", {
                    variant: "success",
                  });

                  if (mounted.current) {
                    setStatus({ success: true });
                    setSubmitting(false);
                  }

                  onSaved && onSaved(repairOperation!);
                  dialogProps?.onClose && dialogProps?.onClose({}, "escapeKeyDown");

                  if (isRedirectToRepairOperation) {
                    history.push(
                      ROUTE_PATH.REPAIR_OPERATION_VIEW(responseRepairOperation.id, {
                        highlightPropsEnc:
                          addedItem &&
                          HighlightHelper.encodePropsForQueryString<RepairOperationViewPageHighlightProps>(
                            {
                              itemId: addedItem?.id,
                            },
                          ),
                      }),
                    );
                  }
                } catch (err) {
                  if (mounted.current) {
                    ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                    setStatus({ success: false });
                    setSubmitting(false);
                  }
                }

                setSubmitting(false);
              }}
            >
              {({ errors, handleSubmit, isSubmitting, values, setFieldValue }) => {
                return (
                  // Don't use form here as we using nested form for repairOperation create
                  <Stack spacing={2}>
                    {/* Damages */}
                    {!isCreatingRepairOperation && (
                      <Stack spacing={2}>
                        <Typography variant='h2'>Damages</Typography>

                        <Stack spacing={1}>
                          {values.vehicleDamages?.map((damage, i) => (
                            <Stack spacing={1} key={i}>
                              <VehicleDamageLink
                                vehicleId={vehicleId}
                                entityId={damage.vehicleDamageId}
                                entity={undefined}
                              />

                              <FormControl fullWidth>
                                <PriceSummaryInput
                                  values={{
                                    currency: values.currency || undefined,
                                    subTotal: values.vehicleDamages?.at(i)?.cost ?? undefined,
                                    discount: values.vehicleDamages?.at(i)?.discount,
                                    tax: values.vehicleDamages?.at(i)?.tax,
                                    total: values.vehicleDamages?.at(i)?.total,
                                  }}
                                  formikProps={{
                                    errors: {
                                      subTotal: getIn(errors, `vehicleDamages[${i}].cost`),
                                      discount: getIn(errors, `vehicleDamages[${i}].discount`),
                                      tax: getIn(errors, `vehicleDamages[${i}].tax`),
                                      total: getIn(errors, `vehicleDamages[${i}].total`),
                                    },
                                  }}
                                  displayProps={{
                                    insurance: false,
                                    calcExplanationInline: false,
                                    calcExplanationModal: true,
                                  }}
                                  inputsProps={{
                                    all: {
                                      margin: "none",
                                      size: "small",
                                    },
                                    subTotal: {
                                      label: "Cost",
                                      required: true,
                                    },
                                    total: {
                                      required: true,
                                    },
                                    allowCurrencyEdit: false,
                                  }}
                                  onChange={(newValue) => {
                                    setFieldValue(`currency`, newValue?.currency);
                                    setFieldValue(`vehicleDamages[${i}].cost`, newValue?.subTotal);
                                    setFieldValue(
                                      `vehicleDamages[${i}].discount`,
                                      newValue?.discount,
                                    );
                                    setFieldValue(`vehicleDamages[${i}].tax`, newValue?.tax);
                                    setFieldValue(`vehicleDamages[${i}].total`, newValue?.total);
                                  }}
                                />
                              </FormControl>
                              {/* {damage?.state === VehicleDamageState.Repaired && (
                              <Alert severity='warning'>This damage is already repaired.</Alert>
                            )} */}
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    )}

                    {/* RepairOperation */}
                    <Box>
                      <FormControl
                        margin='dense'
                        fullWidth
                        error={Boolean(errors.repairOperationId)}
                      >
                        <RepairOperationAutocompleteOrCreate
                          autocompleteProps={{
                            entityId: values.repairOperationId,
                            searchFilters: {
                              vehicleId: vehicleId || undefined,
                              excludeStage: RepairOperationStage.Completed,
                            },
                            onChange: (newValue) => {
                              setFieldValue(`repairOperationId`, newValue?.id);
                              setRepairOperation(newValue || undefined);
                              setFieldValue(`currency`, newValue?.currency);
                            },
                          }}
                          createUpdateProps={{
                            defaultValues: {
                              vehicleId: vehicleId || undefined,
                              vehicleDamageIdsCosts: vehicleDamageIdsCostComputed,
                            },
                          }}
                          createFormPlacement='page'
                          onCreateStart={() => setIsCreatingRepairOperation(true)}
                          onCreate={(newValue) => {
                            setFieldValue(`repairOperationId`, newValue?.id);
                            setRepairOperation(newValue);
                            setIsCreatingRepairOperation(false);

                            handleSubmit();
                          }}
                          onCreateCancel={() => setIsCreatingRepairOperation(false)}
                        />
                        <FormHelperText>{errors.repairOperationId}</FormHelperText>
                      </FormControl>
                    </Box>

                    <GeneralValidationError errors={errors} />

                    {!isCreatingRepairOperation && (
                      <Button
                        sx={{ flex: 1 }}
                        fullWidth
                        color='primary'
                        disabled={!values.repairOperationId}
                        loading={isSubmitting}
                        type='button'
                        variant='contained'
                        onClick={() => handleSubmit()}
                      >
                        Save
                      </Button>
                    )}
                  </Stack>
                );
              }}
            </Formik>
          </Stack>
        </AuthorizedElement>
      </DialogContent>
    </Dialog>
  );
}
